import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";

import { TokenMark } from "@/components/Brand";
import {
  ASQAmount,
  ConsumptionRail,
  SheetLabel,
  Skeleton,
  Status,
  toneForStatus,
  utilityLabel,
} from "@/components/primitives";
import { api, type Utility } from "@/lib/api";
import { formatASQ, idFragment } from "@/lib/format";
import { runtimeConfig } from "@/lib/runtime-config";

export function LandingPage() {
  const config = runtimeConfig();
  const { data, isPending, isError } = useQuery({
    queryKey: ["utilities"],
    queryFn: api.utilities,
  });

  const utilities = data?.utilities ?? [];
  const totalLocked = utilities.reduce((sum, utility) => sum + BigInt(utility.lockedRaw || "0"), 0n);

  return (
    <>
      <section className="directory-hero">
        <div className="sheet directory-hero__layout">
          <div className="directory-hero__copy">
            <p className="live-line">
              <span aria-hidden="true" />
              Live di {config.networkName} · chain {config.chainId}
            </p>

            <h1 className="display-hero">
              Utility properti,
              <span>dibayar dengan ASQ.</span>
            </h1>

            <p className="directory-hero__lead">
              Satu tempat untuk mengaktifkan membership, menukar voucher, dan
              membeli kebutuhan properti. Setiap pembayaran tercatat on-chain
              dan masuk ke vault utility yang permanen.
            </p>

            <div className="directory-hero__actions">
              <Link to="/membership" className="btn btn--primary">
                Mulai dari membership
              </Link>
              <Link to="/protocol" className="btn btn--ghost">
                Periksa protocol
              </Link>
            </div>

            <p className="directory-hero__proof">
              <strong>{utilities.length || 3} utility aktif.</strong> Registry,
              saldo vault, dan status pembayaran dapat diperiksa langsung.
            </p>
          </div>

          <RegistryBoard
            utilities={utilities}
            totalLocked={totalLocked}
            isPending={isPending}
            isError={isError}
          />
        </div>
      </section>

      <section className="sheet utility-directory">
        <div className="directory-heading">
          <h2 className="display-section">Mulai dari kebutuhan Anda.</h2>
          <p>
            Setiap utility punya alur, manfaat, dan vault sendiri. Pilih yang
            ingin digunakan; detail on-chain selalu terlihat sebelum Anda membayar.
          </p>
        </div>

        {isPending ? (
          <div className="utility-directory__loading">
            <Skeleton className="h-72" />
            <Skeleton className="h-64" />
          </div>
        ) : (
          <div className="utility-directory__modules">
            <MembershipPass utility={find(utilities, "ASETQU_MEMBERSHIP")} />
            <div className="utility-directory__split">
              <VoucherTicket utility={find(utilities, "ASETQU_EVOUCHER")} />
              <CatalogIndex utility={find(utilities, "ASETQU_CATALOG")} />
            </div>
          </div>
        )}
      </section>

      <section className="protocol-statement">
        <div className="sheet protocol-statement__layout">
          <div>
            <p className="protocol-statement__label">Jejak pembayaran</p>
            <h2 className="display-section">Masuk sekali. Tercatat selamanya.</h2>
          </div>
          <div>
            <ConsumptionRail from="Wallet" to="Permanent utility vault" />
            <p>
              ASETQUHub tidak menyembunyikan tujuan pembayaran. Module, vault,
              jumlah ASQ, dan status registry ditampilkan sebelum transaksi ditandatangani.
            </p>
            <Link to="/protocol" className="linkline">
              Buka peta protocol →
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function RegistryBoard({
  utilities,
  totalLocked,
  isPending,
  isError,
}: {
  utilities: Utility[];
  totalLocked: bigint;
  isPending: boolean;
  isError: boolean;
}) {
  return (
    <section className="registry-board" aria-labelledby="registry-board-title">
      <header className="registry-board__header">
        <div>
          <p id="registry-board-title">Live utility registry</p>
          <span>Dibaca langsung dari chain</span>
        </div>
        <Status state={isError ? "bad" : isPending ? "idle" : "ok"}>
          {isError ? "Tidak terbaca" : isPending ? "Memuat" : "Online"}
        </Status>
      </header>

      <div className="registry-board__total">
        <TokenMark size={96} decorative />
        <div>
          <p>Total ASQ terkunci</p>
          {isPending ? (
            <Skeleton className="mt-3 h-14 w-64" />
          ) : (
            <ASQAmount raw={totalLocked} size="xl" maxFractionDigits={2} />
          )}
        </div>
      </div>

      <ul className="registry-board__list">
        {isPending
          ? [0, 1, 2].map((entry) => (
              <li key={entry}>
                <Skeleton className="h-8 w-full" />
              </li>
            ))
          : utilities.map((utility) => (
              <li key={utility.id}>
                <div>
                  <span>{utilityLabel(utility.name)}</span>
                  <small>{idFragment(utility.id)}</small>
                </div>
                <Status state={toneForStatus(utility.status)}>{utility.status}</Status>
                <ASQAmount raw={utility.lockedRaw} size="sm" maxFractionDigits={2} />
              </li>
            ))}
      </ul>

      <footer className="registry-board__footer">
        Setiap baris memiliki module dan vault permanen yang berbeda.
      </footer>
    </section>
  );
}

function MembershipPass({ utility }: { utility?: Utility }) {
  return (
    <article className="membership-pass">
      <div className="membership-pass__content">
        <div className="membership-pass__title">
          <SheetLabel coordinate={idFragment(utility?.id)}>Access pass</SheetLabel>
          <h3>Membership ASETQUHub</h3>
          <p>
            Buka harga member, voucher eksklusif, dan akses katalog lebih awal
            selama 30 hari.
          </p>
        </div>

        <div className="membership-pass__action">
          <Link to="/membership" className="btn btn--primary">
            Aktifkan membership
          </Link>
          {utility ? (
            <Status state={toneForStatus(utility.status)}>{utility.status}</Status>
          ) : null}
        </div>
      </div>

      <div className="membership-pass__price">
        <span>Harga tetap</span>
        <ASQAmount raw="3000000000000000000" size="xl" />
        <p>30 hari · satu pembayaran</p>
        <small>
          Vault saat ini: {formatASQ(utility?.lockedRaw, { maxFractionDigits: 2 })} ASQ
        </small>
      </div>
    </article>
  );
}

function VoucherTicket({ utility }: { utility?: Utility }) {
  return (
    <Link to="/vouchers" className="voucher-ticket">
      <div>
        <SheetLabel coordinate={idFragment(utility?.id)}>E-Voucher</SheetLabel>
        <h3>Benefit properti siap ditukar.</h3>
        <p>Interior, inspeksi, legal, dan smart living dengan kode yang terbit setelah verifikasi.</p>
      </div>
      <div className="voucher-ticket__footer">
        <span>{formatASQ(utility?.lockedRaw, { maxFractionDigits: 0 })} ASQ di vault</span>
        <strong>Lihat voucher →</strong>
      </div>
    </Link>
  );
}

function CatalogIndex({ utility }: { utility?: Utility }) {
  return (
    <Link to="/catalog" className="catalog-index">
      <div className="catalog-index__masthead">
        <span>Katalog utility</span>
        <strong>06 produk</strong>
      </div>
      <h3>Produk dan jasa untuk properti Anda.</h3>
      <p>Smart lock, inspeksi, interior, dan audit energi yang dibayar penuh dengan ASQ.</p>
      <div className="catalog-index__footer">
        <span>{formatASQ(utility?.lockedRaw, { maxFractionDigits: 0 })} ASQ terkunci</span>
        <strong>Buka katalog →</strong>
      </div>
    </Link>
  );
}

function find(utilities: Utility[], name: string): Utility | undefined {
  return utilities.find((utility) => utility.name === name);
}
